import React, { useState } from "react";
import { CreditCard, FileText, Clock, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { STATUS_LABELS } from "./constants";

export function OrderActions({ o, onRefresh, onDeleted, showDetails, onToggleDetails }) {
  const [quote, setQuote] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const quoteAmount = (o.quote_amount_after_discount ?? o.quote_amount ?? o.amount) / 100;
  const cardOnFile = !!o.card_on_file;

  const sendQuote = async () => {
    const cents = Math.round(parseFloat(quote) * 100);
    if (!cents || cents <= 0) { toast.error("Enter a valid amount"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/orders/${o.order_id}/quote`, { amount_cents: cents, note });
      toast.success(`Quote $${(cents/100).toFixed(2)} saved on ${o.order_id}`);
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save quote"); }
    finally { setBusy(false); }
  };

  const chargeCard = async () => {
    if (!cardOnFile) { toast.error("No card on file"); return; }
    if (!window.confirm(`Charge $${quoteAmount.toFixed(2)} on the saved card for ${o.order_id}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/orders/${o.order_id}/charge`, {});
      toast.success(`Charged $${(data.amount/100).toFixed(2)} — PI ${data.payment_intent_id}`);
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Charge failed"); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${o.order_id}/status`, { status });
      toast.success(`Status → ${STATUS_LABELS[status] || status}`);
      onRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Status update failed"); }
    finally { setBusy(false); }
  };

  const deleteOrder = async () => {
    if (!window.confirm(`Permanently delete order ${o.order_id}?\n\nThis cannot be undone and removes the record from the database.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/orders/${o.order_id}`);
      toast.success(`Order ${o.order_id} deleted`);
      onDeleted?.(o.order_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded border hairline p-4 bg-[color:var(--muted)]">
      <div className="uppercase text-[10px] tracking-[0.25em] text-[color:var(--text-muted)]"><FileText size={10} className="inline mr-1"/>Send Quote</div>
      <div className="flex gap-2 mt-2 items-center">
        <span className="text-sm">$</span>
        <Input data-testid={`quote-amount-${o.order_id}`} type="number" step="0.01" min="0" value={quote} onChange={(e) => setQuote(e.target.value)} placeholder="0.00" className="h-9"/>
        <Button data-testid={`send-quote-${o.order_id}`} disabled={busy} onClick={sendQuote} className="btn-primary rounded-full h-9 px-4">Send</Button>
      </div>
      <Textarea data-testid={`quote-note-${o.order_id}`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Short note sent to customer (optional)" rows={2} className="mt-2 text-sm bg-white"/>
      <div className="mt-4 pt-4 border-t hairline">
        <div className="uppercase text-[10px] tracking-[0.25em] text-[color:var(--text-muted)]"><CreditCard size={10} className="inline mr-1"/>Charge Saved Card</div>
        <div className="flex items-center justify-between mt-2 gap-3">
          <div className="text-sm">
            {cardOnFile ? (<>Charge <b>${quoteAmount.toFixed(2)}</b> off-session</>) : (<span className="text-[color:var(--text-muted)]">No card on file yet</span>)}
          </div>
          <Button data-testid={`charge-${o.order_id}`} disabled={busy || !cardOnFile || !quoteAmount} onClick={chargeCard} className="btn-metal rounded-full h-9 px-4">
            <CreditCard size={14} className="mr-2"/>Charge
          </Button>
        </div>
        {o.charge_last_error && <div className="text-xs text-rose-700 mt-2">Last error: {o.charge_last_error}</div>}
      </div>
      <div className="mt-4 pt-4 border-t hairline">
        <div className="uppercase text-[10px] tracking-[0.25em] text-[color:var(--text-muted)]"><Clock size={10} className="inline mr-1"/>Set Status</div>
        <div className="flex gap-2 mt-2 flex-wrap">
          {["in_production", "shipped", "delivered", "cancelled"].map((s) => (
            <Button key={s} data-testid={`status-${s}-${o.order_id}`} variant="outline" size="sm" disabled={busy} onClick={() => setStatus(s)} className="rounded-full h-8 text-xs">
              {STATUS_LABELS[s]}
            </Button>
          ))}
        </div>
      </div>
      <div className="mt-4 pt-4 border-t hairline flex items-center justify-between gap-3">
        <Button data-testid={`view-details-${o.order_id}`} variant="outline" size="sm" onClick={onToggleDetails} className="rounded-full h-8 text-xs">
          {showDetails ? <ChevronUp size={12} className="mr-1"/> : <ChevronDown size={12} className="mr-1"/>}
          {showDetails ? "Hide details" : "View all details"}
        </Button>
        <Button data-testid={`delete-order-${o.order_id}`} variant="ghost" size="sm" disabled={busy} onClick={deleteOrder} className="rounded-full h-8 text-xs text-rose-700 hover:text-rose-900 hover:bg-rose-50">
          <Trash2 size={12} className="mr-1"/>Delete order
        </Button>
      </div>
    </div>
  );
}
